// SSE Event types matching backend OrderStreamController
export interface NewOrderEvent {
  orderId: number
  restaurantId: number
  total: number
  orderType: 'delivery' | 'pickup' | 'dineIn'
  status: string
  telephone: string
  timestamp: number
}

export interface OrderStatusEvent {
  orderId: number
  restaurantId: number
  status: string
  timestamp: number
}

export interface RealtimeOrderBatch {
  events: NewOrderEvent[]
  latestOrderId: number
  hasMore: boolean
}

export type DataChangedDomain =
  | 'opening_times'
  | 'menu'
  | 'general_settings'
  | 'delivery'
  | 'vouchers'
  | 'holidays'
  | 'orders'
  | 'reservations'

export interface DataChangedEvent {
  restaurantId: number
  domain: DataChangedDomain
  resources: string[]
  timestamp: number
}

export type OrderStreamEvent = NewOrderEvent | OrderStatusEvent | DataChangedEvent

// Connection status for SSE stream
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

// Notification settings stored in localStorage
export interface NotificationSettings {
  enabled: boolean
  soundEnabled: boolean
  browserNotificationsEnabled: boolean
  repeatInterval: number // seconds, 0 = no repeat
}

// Notification item for display
export interface OrderNotification {
  id: string
  orderId: number
  type: 'new-order' | 'status-change'
  message: string
  timestamp: number
  acknowledged: boolean
  orderType?: 'delivery' | 'pickup' | 'dineIn'
  total?: number
}

// Actions for notification reducer
export type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: OrderNotification }
  | { type: 'ACKNOWLEDGE_NOTIFICATION'; payload: string }
  | { type: 'ACKNOWLEDGE_ALL' }
  | { type: 'ACKNOWLEDGE_ORDERS'; payload: number[] }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_SETTINGS'; payload: Partial<NotificationSettings> }
  | { type: 'SET_CONNECTION_STATUS'; payload: ConnectionStatus }

// Notification state
export interface NotificationState {
  notifications: OrderNotification[]
  settings: NotificationSettings
  connectionStatus: ConnectionStatus
  unreadCount: number
}

// Default settings
export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  enabled: true,
  soundEnabled: true,
  browserNotificationsEnabled: false,
  repeatInterval: 60, // Repeat every 60 seconds for unacknowledged orders
}

// Server-side notification settings from backend API
// Maps to NotificationsettingsRow in Scala backend
export interface ServerNotificationSettings {
  restaurantId: number
  repeatingNotifications: boolean
  repeatingInterval: string // HH:MM:SS format (SQL time)
}

// Request type for updating server settings (form-encoded)
export interface UpdateServerNotificationSettingsRequest {
  restaurant_id: number
  repeatingNotifications: boolean
  repeatingInterval: string // HH:MM:SS format
}

// Helper to convert seconds to HH:MM:SS format
export function secondsToTimeString(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return [hours, minutes, secs]
    .map((v) => v.toString().padStart(2, '0'))
    .join(':')
}

// Helper to convert HH:MM:SS to seconds
export function timeStringToSeconds(timeStr: string): number {
  const parts = timeStr.split(':').map(Number)
  if (parts.length !== 3) return 60 // Default 1 minute
  return parts[0] * 3600 + parts[1] * 60 + parts[2]
}
